import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  walkthroughService,
  type Walkthrough,
  type WalkthroughStep,
  type WalkthroughListResult,
  type CreateWalkthroughInput,
  type UpdateWalkthroughInput,
  type CreateStepInput,
  type UpdateStepInput,
  type CreateTriggerInput,
} from '@/services/walkthrough.service';
import { walkthroughKeys } from './queryKeys';

export { walkthroughKeys };

function errorMessage(error: unknown, fallback: string): string {
  const message = (
    error as { response?: { data?: { message?: string | string[] } } }
  )?.response?.data?.message;
  if (Array.isArray(message)) return message[0] ?? fallback;
  return message ?? fallback;
}

// ==================== ADMIN ====================

export function useWalkthroughs(filters: {
  page?: number;
  perPage?: number;
  status?: string;
  search?: string;
}) {
  return useQuery<WalkthroughListResult>({
    queryKey: walkthroughKeys.list(filters),
    queryFn: () => walkthroughService.list(filters),
  });
}

export function useWalkthrough(
  id: string | undefined,
  options?: Partial<UseQueryOptions<Walkthrough>>,
) {
  return useQuery<Walkthrough>({
    queryKey: walkthroughKeys.detail(id ?? ''),
    queryFn: () => walkthroughService.getById(id as string),
    enabled: !!id,
    ...options,
  });
}

export function useCreateWalkthrough() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWalkthroughInput) =>
      walkthroughService.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: walkthroughKeys.all });
      toast.success('Tour criado');
    },
    onError: (e) => toast.error(errorMessage(e, 'Erro ao criar tour')),
  });
}

export function useUpdateWalkthrough(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWalkthroughInput) =>
      walkthroughService.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: walkthroughKeys.all });
      toast.success('Tour atualizado');
    },
    onError: (e) => toast.error(errorMessage(e, 'Erro ao atualizar tour')),
  });
}

export function useDeleteWalkthrough() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => walkthroughService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: walkthroughKeys.all });
      toast.success('Tour excluído');
    },
    onError: (e) => toast.error(errorMessage(e, 'Erro ao excluir tour')),
  });
}

export function useDuplicateWalkthrough() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => walkthroughService.duplicate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: walkthroughKeys.all });
      toast.success('Tour duplicado');
    },
    onError: (e) => toast.error(errorMessage(e, 'Erro ao duplicar tour')),
  });
}

// ==================== STEPS ====================

export function useAddWalkthroughStep(walkthroughId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStepInput) =>
      walkthroughService.addStep(walkthroughId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: walkthroughKeys.all });
      toast.success('Passo adicionado');
    },
    onError: (e) => toast.error(errorMessage(e, 'Erro ao adicionar passo')),
  });
}

export function useUpdateWalkthroughStep(walkthroughId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      stepId,
      input,
    }: {
      stepId: string;
      input: UpdateStepInput;
    }) => walkthroughService.updateStep(stepId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: walkthroughKeys.detail(walkthroughId) });
      toast.success('Passo atualizado');
    },
    onError: (e) => toast.error(errorMessage(e, 'Erro ao atualizar passo')),
  });
}

export function useDeleteWalkthroughStep(walkthroughId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stepId: string) => walkthroughService.deleteStep(stepId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: walkthroughKeys.detail(walkthroughId) });
      toast.success('Passo removido');
    },
    onError: (e) => toast.error(errorMessage(e, 'Erro ao remover passo')),
  });
}

/** Reordena os passos de forma otimista, com rollback em caso de erro. */
export function useReorderWalkthroughSteps(walkthroughId: string) {
  const qc = useQueryClient();
  const key = walkthroughKeys.detail(walkthroughId);

  return useMutation({
    mutationFn: (ids: string[]) =>
      walkthroughService.reorderSteps(walkthroughId, ids),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<Walkthrough>(key);
      if (prev?.steps) {
        const byId = new Map(prev.steps.map((s) => [s.id, s]));
        const next = ids
          .map((id, idx) => {
            const s = byId.get(id);
            return s ? { ...s, order: idx } : undefined;
          })
          .filter((s): s is WalkthroughStep => !!s);
        qc.setQueryData<Walkthrough>(key, { ...prev, steps: next });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast.error('Erro ao reordenar passos');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

// ==================== TRIGGERS ====================

export function useAddWalkthroughTrigger(walkthroughId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTriggerInput) =>
      walkthroughService.addTrigger(walkthroughId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: walkthroughKeys.detail(walkthroughId) });
      toast.success('Gatilho adicionado');
    },
    onError: (e) => toast.error(errorMessage(e, 'Erro ao adicionar gatilho')),
  });
}

export function useDeleteWalkthroughTrigger(walkthroughId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (triggerId: string) =>
      walkthroughService.deleteTrigger(triggerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: walkthroughKeys.detail(walkthroughId) });
      toast.success('Gatilho removido');
    },
    onError: (e) => toast.error(errorMessage(e, 'Erro ao remover gatilho')),
  });
}

// ==================== ALUNO ====================

export function useMyPendingWalkthroughs(enabled: boolean) {
  return useQuery<Walkthrough[]>({
    queryKey: walkthroughKeys.myPending(),
    queryFn: () => walkthroughService.getMyPending(),
    enabled,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
}

export function useResetMyWalkthroughs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (walkthroughId?: string) =>
      walkthroughService.resetMyProgress(walkthroughId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: walkthroughKeys.myPending() });
      toast.success('Tutorial reiniciado');
    },
    onError: (e) => toast.error(errorMessage(e, 'Erro ao reiniciar tutorial')),
  });
}
